import { Component, type ErrorInfo, type ReactNode } from "react";
import { captureException } from "@/lib/Analytics";
import { ErrorFallback } from "./ErrorFallback";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
}

/**
 * 앱 전체를 감싸는 최상위 에러 바운더리. 렌더 중 던져진 에러를 잡아 흰 화면 대신
 * 복구용 폴백을 보여주고, PostHog Error tracking 으로 예외를 올린다.
 *
 * main.tsx 에서 <App/> 을 감싼다 — App.tsx 가 로그인·온보딩·초대 등 여러 화면을
 * 라우터 진입 전에 early-return 하므로, TanStack Router 의 라우트 레벨
 * errorComponent 로는 그 화면들이 안 덮인다. i18n·Query Provider 안쪽에 두어
 * 폴백에서 <Trans> 를 쓸 수 있게 한다(OfflineToast 를 형제로 둔 이유와 같다).
 *
 * React 19 는 바운더리가 잡은 에러를 window.onerror 로 다시 던지지 않으므로
 * (init 의 capture_exceptions 가 못 봄) componentDidCatch 에서 직접
 * captureException 으로 올린다(lib/Analytics.ts). 복구는 상태 리셋이 아니라
 * 하드 리로드로 한다 — 렌더가 깨진 원인(오래된 청크·깨진 캐시)이 그대로면
 * 되돌려도 다시 터지기 때문이다.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: ErrorInfo): void {
    // develop 채널에서는 렌더 에러를 크롬 콘솔에도 찍어 dev 서버처럼 바로 보이게 한다 —
    // React 19 는 바운더리가 잡은 에러를 콘솔로 자동 출력하지 않으므로 명시한다. develop
    // 은 prod 와 분리된 스테이징이라 노이즈를 감수한다. nightly 는 콘솔을 숨기는 채널이라
    // (빌드 시 console drop, vite.config.ts) 여기서 제외하고, prod 도 콘솔에 남기지 않는다.
    // 에러 수집 자체는 채널과 무관하게 아래 captureException 이 전담한다.
    if (import.meta.env.VITE_PUBLIC_APP_ENV === "develop") {
      console.error("[react-error-boundary]", error, info.componentStack);
    }
    captureException(error, {
      source: "react-error-boundary",
      // PostHog 이슈 상세에서 어느 컴포넌트 트리에서 터졌는지 보이도록 넘긴다.
      $exception_component_stack: info.componentStack,
    });
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return <ErrorFallback />;
  }
}
